import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Box, Button, Alert, Snackbar, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Add,
  AutoAwesome,
  Bolt,
  ChatBubbleOutline,
  Collections,
  Extension,
  Favorite,
  Groups,
  Inventory2,
  Payments,
  People,
  Person,
  Settings,
  LocalOffer,
  SportsEsports,
  Wallpaper,
  ViewQuilt,
  Animation,
  Checkroom
} from '@mui/icons-material';
import { UserButton, useAuth } from '@clerk/clerk-react';
import CharacterList from '../components/CharacterList';
import CharacterForm from '../components/CharacterForm';
import KinkList from '../components/KinkList';
import KinkForm from '../components/KinkForm';
import GrokRequestForm from '../components/GrokRequestForm';
import ItemList from '../components/ItemList';
import SetList from '../components/SetList';
import FlowList from '../components/FlowList';
import FlowForm from '../components/FlowForm';
import FlowEditor from '../components/FlowEditor';
import FlowBackupList from '../components/FlowBackupList';
import GameConfig from '../components/GameConfig';
import TriggerList from '../components/TriggerList';
import TriggerForm from '../components/TriggerForm';
import BackgroundList from '../components/BackgroundList';
import BackgroundForm from '../components/BackgroundForm';
import SceneList from '../components/SceneList';
import SceneForm from '../components/SceneForm';
import SceneMetaForm from '../components/SceneMetaForm';
import CurrencyPackList from '../components/CurrencyPackList';
import UserManagement from '../components/UserManagement';
import PlayersPanel from '../components/PlayersPanel';
import PromoCodesPanel from '../components/PromoCodesPanel';
import AnimControllersPanel from '../components/AnimControllersPanel';
import RealItemList from '../components/RealItemList';
import RealItemForm from '../components/RealItemForm';
import JoiPanel from '../components/JoiPanel';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';

const AdminPage = ({ userRole }) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [characters, setCharacters] = useState([]);
  const [kinks, setKinks] = useState([]);
  const [items, setItems] = useState([]);
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(true);
  const [flowEditorOpen, setFlowEditorOpen] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [selectedFlowForBackups, setSelectedFlowForBackups] = useState(null);
  const [flowFormOpen, setFlowFormOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggerFormOpen, setTriggerFormOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [backgroundsLoading, setBackgroundsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [bgAssetsTab, setBgAssetsTab] = useState('backgrounds');
  const [backgroundFormOpen, setBackgroundFormOpen] = useState(false);
  const [editingBgAsset, setEditingBgAsset] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [scenesLoading, setScenesLoading] = useState(true);
  const [sceneFormOpen, setSceneFormOpen] = useState(false);
  const [sceneSubmitting, setSceneSubmitting] = useState(false);
  const [sceneMetaFormOpen, setSceneMetaFormOpen] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [sceneMetaSubmitting, setSceneMetaSubmitting] = useState(false);
  const [currencyPacksLoading, setCurrencyPacksLoading] = useState(true);
  const [charactersLoading, setCharactersLoading] = useState(true);
  const [kinksLoading, setKinksLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [setsLoading, setSetsLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [kinkFormOpen, setKinkFormOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [editingKink, setEditingKink] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [realItems, setRealItems] = useState([]);
  const [realItemsLoading, setRealItemsLoading] = useState(true);
  const [realItemFormOpen, setRealItemFormOpen] = useState(false);
  const [editingRealItem, setEditingRealItem] = useState(null);

  const navItems = useMemo(() => {
    const gameHref = `${process.env.PUBLIC_URL || ''}/game`;
    const openGame = {
      id: 'open-game',
      label: 'Open Game',
      Icon: SportsEsports,
      externalHref: gameHref
    };
    const rest = [
      { id: 'characters', label: 'Characters', Icon: Person },
      { id: 'kinks', label: 'Kinks', Icon: Extension },
      { id: 'joi', label: 'JOI', Icon: Favorite },
      { id: 'realitems', label: 'Real Items', Icon: Checkroom },
      { id: 'items', label: 'Items', Icon: Inventory2 },
      { id: 'sets', label: 'Sets', Icon: Collections },
      { id: 'dialogues', label: 'Dialogues', Icon: ChatBubbleOutline },
      { id: 'triggers', label: 'Triggers', Icon: Bolt },
      { id: 'backgrounds', label: 'Backgrounds', Icon: Wallpaper },
      { id: 'scenes', label: 'Scenes', Icon: ViewQuilt },
      { id: 'animations', label: 'Animations', Icon: Animation },
      { id: 'currency', label: 'Currency Packs', Icon: Payments },
      { id: 'promocodes', label: 'Promo Codes', Icon: LocalOffer },
      { id: 'gameconfig', label: 'Game Config', Icon: Settings },
      { id: 'players', label: 'Players', Icon: Groups },
      { id: 'ai', label: 'AI Requests', Icon: AutoAwesome },
      openGame
    ];
    if (userRole === 'admin') {
      return [{ id: 'users', label: 'Users', Icon: People }, ...rest];
    }
    return rest;
  }, [userRole]);

  const [activeNavId, setActiveNavId] = useState(() =>
    userRole === 'admin' ? 'users' : 'characters'
  );

  // Load characters
  const fetchCharacters = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/characters`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCharacters(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading characters:', error);
      showSnackbar('Error loading characters', 'error');
      setCharacters([]);
    } finally {
      setCharactersLoading(false);
    }
  };

  // Load kinks
  const fetchKinks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kinks`);
      const data = await response.json();
      setKinks(data);
    } catch (error) {
      showSnackbar('Error loading kinks', 'error');
    } finally {
      setKinksLoading(false);
    }
  };

  // Load items
  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      showSnackbar('Error loading items', 'error');
    } finally {
      setItemsLoading(false);
    }
  };

  const fetchRealItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/real-items`);
      const data = await response.json();
      setRealItems(Array.isArray(data) ? data : []);
    } catch (error) {
      showSnackbar('Error loading real items', 'error');
    } finally {
      setRealItemsLoading(false);
    }
  };

  const fetchSets = async () => {
    setSetsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sets`);
      if (!response.ok) throw new Error('Bad response');
      await response.json();
    } catch (error) {
      showSnackbar('Error loading sets', 'error');
    } finally {
      setSetsLoading(false);
    }
  };

  // Load flows
  const fetchFlows = async () => {
    setFlowsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/flows`);
      const data = await response.json();
      setFlows(Array.isArray(data) ? data : []);
      if (!response.ok) {
        showSnackbar(data?.message || 'Error loading flows', 'error');
      }
    } catch (error) {
      setFlows([]);
      showSnackbar('Error loading flows', 'error');
    } finally {
      setFlowsLoading(false);
    }
  };

  // Load triggers
  const fetchTriggers = async () => {
    setTriggersLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/triggers`);
      const data = await response.json();
      setTriggers(data);
    } catch (error) {
      showSnackbar('Error loading triggers', 'error');
    } finally {
      setTriggersLoading(false);
    }
  };

  const fetchBackgrounds = async () => {
    setBackgroundsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/backgrounds`);
      const data = await response.json();
      setBackgrounds(Array.isArray(data) ? data : []);
    } catch (error) {
      showSnackbar('Error loading backgrounds', 'error');
      setBackgrounds([]);
    } finally {
      setBackgroundsLoading(false);
    }
  };

  const fetchLocations = async () => {
    setLocationsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/locations`);
      const data = await response.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      showSnackbar('Error loading locations', 'error');
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchScenes = async () => {
    setScenesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/scenes`);
      const data = await response.json();
      setScenes(Array.isArray(data) ? data : []);
    } catch (error) {
      showSnackbar('Error loading scenes', 'error');
      setScenes([]);
    } finally {
      setScenesLoading(false);
    }
  };

  // Load currency packs
  const fetchCurrencyPacks = async () => {
    setCurrencyPacksLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/currency-packs`);
      if (!response.ok) throw new Error('Bad response');
      await response.json();
    } catch (error) {
      showSnackbar('Error loading currency packs', 'error');
    } finally {
      setCurrencyPacksLoading(false);
    }
  };

  useEffect(() => {
    fetchCharacters();
    fetchKinks();
    fetchItems();
    fetchSets();
    fetchFlows();
    fetchTriggers();
    fetchBackgrounds();
    fetchLocations();
    fetchScenes();
    fetchCurrencyPacks();
    fetchRealItems();
    // Intentionally once on mount; fetch* close over latest showSnackbar after first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const navId = locationState && locationState.adminNavId;
    if (navId) {
      setActiveNavId(navId);
    }
  }, [locationState]);

  useEffect(() => {
    if (activeNavId !== 'backgrounds') {
      setBgAssetsTab('backgrounds');
      setBackgroundFormOpen(false);
      setEditingBgAsset(null);
    }
  }, [activeNavId]);

  const uploadCharacterPortrait = async (characterId, kind, file) => {
    if (!file || !characterId) return;
    const token = await getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE_URL}/api/characters/${characterId}/avatar-${kind}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Portrait upload failed (${kind})`);
    }
  };

  // Create character
  const handleCreateCharacter = async (characterData) => {
    const avatarGridFile = characterData.avatarGridFile;
    const avatarChatFile = characterData.avatarChatFile;
    const payload = { ...characterData };
    delete payload.avatarGridFile;
    delete payload.avatarChatFile;
    delete payload.clearAvatarGrid;
    delete payload.clearAvatarChat;
    try {
      const response = await fetch(`${API_BASE_URL}/api/characters`, {
        method: 'POST',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Error creating character');
      }

      const created = await response.json();
      const id = created._id;
      await uploadCharacterPortrait(id, 'grid', avatarGridFile);
      await uploadCharacterPortrait(id, 'chat', avatarChatFile);
      await fetchCharacters();
      showSnackbar('Character created successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Update character
  const handleUpdateCharacter = async (characterData) => {
    const { avatarGridFile, avatarChatFile, clearAvatarGrid, clearAvatarChat, ...rest } = characterData;
    try {
      const response = await fetch(`${API_BASE_URL}/api/characters/${editingCharacter._id}`, {
        method: 'PUT',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify({
          ...rest,
          clearAvatarGrid: Boolean(clearAvatarGrid),
          clearAvatarChat: Boolean(clearAvatarChat)
        })
      });

      if (!response.ok) {
        throw new Error('Error updating character');
      }

      const id = editingCharacter._id;
      await uploadCharacterPortrait(id, 'grid', avatarGridFile);
      await uploadCharacterPortrait(id, 'chat', avatarChatFile);
      await fetchCharacters();
      showSnackbar('Character updated successfully', 'success');
      setEditingCharacter(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Delete character
  const handleDeleteCharacter = async (characterId) => {
    if (!window.confirm('Are you sure you want to delete this character?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/characters/${characterId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken),
      });

      if (!response.ok) {
        throw new Error('Error deleting character');
      }

      await fetchCharacters();
      showSnackbar('Character deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Create kink
  const handleCreateKink = async (kinkData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kinks`, {
        method: 'POST',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify(kinkData),
      });

      if (!response.ok) {
        throw new Error('Error creating kink');
      }

      await fetchKinks();
      showSnackbar('Kink created successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Update kink
  const handleUpdateKink = async (kinkData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/kinks/${editingKink._id}`, {
        method: 'PUT',
        headers: await getAuthHeaders(getToken),
        body: JSON.stringify(kinkData),
      });

      if (!response.ok) {
        throw new Error('Error updating kink');
      }

      await fetchKinks();
      showSnackbar('Kink updated successfully', 'success');
      setEditingKink(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Delete kink
  const handleDeleteKink = async (kinkId) => {
    if (!window.confirm('Are you sure you want to delete this kink?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/kinks/${kinkId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken),
      });

      if (!response.ok) {
        throw new Error('Error deleting kink');
      }

      await fetchKinks();
      showSnackbar('Kink deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Real Items CRUD
  const handleOpenRealItemForm = () => { setEditingRealItem(null); setRealItemFormOpen(true); };
  const handleEditRealItem = (item) => { setEditingRealItem(item); setRealItemFormOpen(true); };
  const handleSubmitRealItem = async (data) => {
    try {
      const url = editingRealItem
        ? `${API_BASE_URL}/api/real-items/${editingRealItem._id}`
        : `${API_BASE_URL}/api/real-items`;
      const method = editingRealItem ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders(getToken)) },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Error saving real item');
      await fetchRealItems();
      showSnackbar(editingRealItem ? 'Real item updated' : 'Real item created', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };
  const handleDeleteRealItem = async (id) => {
    if (!window.confirm('Delete this real item?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/real-items/${id}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken)
      });
      if (!response.ok) throw new Error('Error deleting real item');
      await fetchRealItems();
      showSnackbar('Real item deleted', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Create flow
  const handleCreateFlow = async (flowData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/flows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(flowData),
      });

      if (!response.ok) {
        throw new Error('Error creating flow');
      }

      await fetchFlows();
      showSnackbar('Flow created successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Update flow
  const handleUpdateFlow = async (flowData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/flows/${editingFlow._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(flowData),
      });

      if (!response.ok) {
        throw new Error('Error updating flow');
      }

      await fetchFlows();
      showSnackbar('Flow updated successfully', 'success');
      setEditingFlow(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Delete flow
  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm('Are you sure you want to delete this flow?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Error deleting flow');
      }

      await fetchFlows();
      showSnackbar('Flow deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Create trigger
  const handleCreateTrigger = async (triggerData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/triggers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(triggerData),
      });

      if (!response.ok) {
        throw new Error('Error creating trigger');
      }

      await fetchTriggers();
      showSnackbar('Trigger created successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Update trigger
  const handleUpdateTrigger = async (triggerData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/triggers/${editingTrigger._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(triggerData),
      });

      if (!response.ok) {
        throw new Error('Error updating trigger');
      }

      await fetchTriggers();
      showSnackbar('Trigger updated successfully', 'success');
      setEditingTrigger(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  // Delete trigger
  const handleDeleteTrigger = async (triggerId) => {
    if (!window.confirm('Are you sure you want to delete this trigger?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/triggers/${triggerId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Error deleting trigger');
      }

      await fetchTriggers();
      showSnackbar('Trigger deleted successfully', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleCreateBackground = async ({ kind, file }) => {
    if (!file) {
      showSnackbar('Select an image file', 'error');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('kind', kind === 'slide' ? 'slide' : 'background');
      const response = await fetch(`${API_BASE_URL}/api/backgrounds`, {
        method: 'POST',
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error creating background');
      }
      await fetchBackgrounds();
      showSnackbar('Background image uploaded (R2)', 'success');
      setBackgroundFormOpen(false);
      setEditingBgAsset(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleUpdateBackground = async ({ kind, file }) => {
    try {
      const fd = new FormData();
      fd.append('kind', kind === 'slide' ? 'slide' : 'background');
      if (file) {
        fd.append('image', file);
      }
      const response = await fetch(`${API_BASE_URL}/api/backgrounds/${editingBgAsset._id}`, {
        method: 'PUT',
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error updating background');
      }
      await fetchBackgrounds();
      showSnackbar('Background updated', 'success');
      setBackgroundFormOpen(false);
      setEditingBgAsset(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleDeleteBackground = async (id) => {
    if (!window.confirm('Delete this background? Flow nodes may reference it.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/backgrounds/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting background');
      await fetchBackgrounds();
      showSnackbar('Background deleted', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleCreateLocation = async ({ label, file }) => {
    if (!file) {
      showSnackbar('Select an image file', 'error');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('label', label);
      const response = await fetch(`${API_BASE_URL}/api/locations`, {
        method: 'POST',
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error creating location');
      }
      await fetchLocations();
      showSnackbar('Location image uploaded (R2)', 'success');
      setBackgroundFormOpen(false);
      setEditingBgAsset(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleUpdateLocation = async ({ label, file }) => {
    try {
      const fd = new FormData();
      fd.append('label', label);
      if (file) {
        fd.append('image', file);
      }
      const response = await fetch(`${API_BASE_URL}/api/locations/${editingBgAsset._id}`, {
        method: 'PUT',
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error updating location');
      }
      await fetchLocations();
      showSnackbar('Location updated', 'success');
      setBackgroundFormOpen(false);
      setEditingBgAsset(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/locations/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting location');
      await fetchLocations();
      showSnackbar('Location deleted', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleEditBgAsset = (row) => {
    setEditingBgAsset(row);
    setBackgroundFormOpen(true);
  };

  const handleOpenBackgroundForm = () => {
    setEditingBgAsset(null);
    setBackgroundFormOpen(true);
  };

  const handleCloseBackgroundForm = () => {
    setBackgroundFormOpen(false);
    setEditingBgAsset(null);
  };

  const handleDeleteScene = async (id) => {
    if (!window.confirm('Delete this scene?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/scenes/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting scene');
      await fetchScenes();
      showSnackbar('Scene deleted', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleOpenSceneCanvas = (s) => {
    navigate(`/scenes/${s._id}/canvas`);
  };

  const handleOpenSceneForm = () => {
    setSceneFormOpen(true);
  };

  const handleCloseSceneForm = () => {
    if (!sceneSubmitting) setSceneFormOpen(false);
  };

  const handleCreateScene = async (payload) => {
    setSceneSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/scenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error creating scene');
      }
      await fetchScenes();
      showSnackbar('Scene created', 'success');
      setSceneFormOpen(false);
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setSceneSubmitting(false);
    }
  };

  const handleOpenSceneMetaForm = (s) => {
    setEditingScene(s);
    setSceneMetaFormOpen(true);
  };

  const handleCloseSceneMetaForm = () => {
    if (!sceneMetaSubmitting) {
      setSceneMetaFormOpen(false);
      setEditingScene(null);
    }
  };

  const handleUpdateSceneMeta = async ({ _id, name, characterId, flowId, flow2Id, flow3Id }) => {
    setSceneMetaSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/scenes/${_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, characterId, flowId, flow2Id, flow3Id })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Error updating scene');
      }
      await fetchScenes();
      showSnackbar('Scene updated', 'success');
      setSceneMetaFormOpen(false);
      setEditingScene(null);
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setSceneMetaSubmitting(false);
    }
  };

  const handleSubmitBackgroundForm = (payload) => {
    const isLoc = bgAssetsTab === 'locations';
    if (isLoc) {
      if (editingBgAsset) {
        handleUpdateLocation(payload);
      } else {
        handleCreateLocation(payload);
      }
    } else if (editingBgAsset) {
      handleUpdateBackground(payload);
    } else {
      handleCreateBackground(payload);
    }
  };

  // Edit character
  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    setFormOpen(true);
  };

  // Edit kink
  const handleEditKink = (kink) => {
    setEditingKink(kink);
    setKinkFormOpen(true);
  };

  // Edit flow
  const handleEditFlow = (flow) => {
    setEditingFlow(flow);
    setFlowFormOpen(true);
  };



  // Edit trigger
  const handleEditTrigger = (trigger) => {
    setEditingTrigger(trigger);
    setTriggerFormOpen(true);
  };

  // Open create form
  const handleOpenForm = () => {
    setEditingCharacter(null);
    setFormOpen(true);
  };

  // Open create kink form
  const handleOpenKinkForm = () => {
    setEditingKink(null);
    setKinkFormOpen(true);
  };

  // Open create flow form
  const handleOpenFlowForm = () => {
    setEditingFlow(null);
    setFlowFormOpen(true);
  };

  // Open create trigger form
  const handleOpenTriggerForm = () => {
    setEditingTrigger(null);
    setTriggerFormOpen(true);
  };

  // Close form
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCharacter(null);
  };

  // Close kink form
  const handleCloseKinkForm = () => {
    setKinkFormOpen(false);
    setEditingKink(null);
  };

  // Close flow form
  const handleCloseFlowForm = () => {
    setFlowFormOpen(false);
    setEditingFlow(null);
  };

  // Close trigger form
  const handleCloseTriggerForm = () => {
    setTriggerFormOpen(false);
    setEditingTrigger(null);
  };


  // Submit form
  const handleSubmitForm = (characterData) => {
    if (editingCharacter) {
      handleUpdateCharacter(characterData);
    } else {
      handleCreateCharacter(characterData);
    }
  };

  // Submit kink form
  const handleSubmitKinkForm = (kinkData) => {
    if (editingKink) {
      handleUpdateKink(kinkData);
    } else {
      handleCreateKink(kinkData);
    }
  };

  // Submit flow form
  const handleSubmitFlowForm = (flowData) => {
    if (editingFlow) {
      handleUpdateFlow(flowData);
    } else {
      handleCreateFlow(flowData);
    }
  };

  // Submit trigger form
  const handleSubmitTriggerForm = (triggerData) => {
    if (editingTrigger) {
      handleUpdateTrigger(triggerData);
    } else {
      handleCreateTrigger(triggerData);
    }
  };

  // Show notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Open flow editor
  const handleOpenFlowEditor = (flow) => {
    setCurrentFlow(flow);
    setFlowEditorOpen(true);
  };

  const handleCloseFlowEditor = () => {
    setFlowEditorOpen(false);
    setCurrentFlow(null);
  };

  const handleBackups = (flow) => {
    setSelectedFlowForBackups(flow);
    setBackupDialogOpen(true);
  };

  const handleCloseBackupDialog = () => {
    setBackupDialogOpen(false);
    setSelectedFlowForBackups(null);
  };

  const panelClass =
    'rounded-xl border border-zinc-800/90 bg-zinc-900/50 p-5 shadow-lg shadow-black/25 backdrop-blur-sm';

  const navRowBase =
    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition-colors';
  const navRowInactive = `${navRowBase} border border-transparent text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-200`;
  const navRowActive = `${navRowBase} border border-violet-500/35 bg-violet-500/15 text-violet-200`;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-zinc-950 text-zinc-200">
      <aside className="flex h-full w-56 shrink-0 flex-col border-r border-zinc-800/90 bg-zinc-900/70">
        <div className="border-b border-zinc-800/90 px-2.5 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-500 leading-tight">
            Velvet Room
          </div>
          <div className="mt-0.5 text-xs font-semibold text-zinc-100 leading-tight">Admin</div>
        </div>
        <nav className="vr-scroll-y min-h-0 flex-1 space-y-px overflow-y-auto p-1.5">
          {navItems.map((item) => {
            const NavIcon = item.Icon;
            const active = activeNavId === item.id;
            if (item.externalHref) {
              return (
                <a
                  key={item.id}
                  href={item.externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navRowInactive}
                >
                  {NavIcon ? (
                    <NavIcon
                      sx={{
                        fontSize: 17,
                        flexShrink: 0,
                        color: 'currentColor'
                      }}
                    />
                  ) : null}
                  <span className="min-w-0 leading-snug">{item.label}</span>
                </a>
              );
            }
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveNavId(item.id)}
                className={active ? navRowActive : navRowInactive}
              >
                {NavIcon ? (
                  <NavIcon
                    sx={{
                      fontSize: 17,
                      flexShrink: 0,
                      color: active ? 'rgb(221 214 254)' : 'currentColor'
                    }}
                  />
                ) : null}
                <span className="min-w-0 leading-snug">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="border-t border-zinc-800/90 px-1.5 py-1">
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: {
                userButtonBox: 'flex justify-center',
                userButtonTrigger: 'focus:shadow-none p-0.5',
                userButtonAvatarBox: 'w-7 h-7'
              }
            }}
          />
        </div>
      </aside>

      <main className="vr-scroll-y min-h-0 min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-5 py-5">

      {/* Users (Admin only) */}
      {userRole === 'admin' && activeNavId === 'users' && (
        <div className={panelClass}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}
          >
            Velvet Room Team
          </Typography>
          <UserManagement />
        </div>
      )}

      {/* Characters */}
      {activeNavId === 'characters' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenForm}>
              Add Character
            </Button>
          </Box>

          {charactersLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <CharacterList
              characters={characters}
              onEdit={handleEditCharacter}
              onDelete={handleDeleteCharacter}
            />
          )}
        </div>
      )}

      {/* Kinks */}
      {activeNavId === 'kinks' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenKinkForm}>
              Add Kink
            </Button>
          </Box>

          {kinksLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <KinkList
              kinks={kinks}
              onEdit={handleEditKink}
              onDelete={handleDeleteKink}
            />
          )}
        </div>
      )}

      {/* Items */}
      {activeNavId === 'items' && (
        <div className={panelClass}>
          {itemsLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <ItemList />
          )}
        </div>
      )}

      {/* Real Items */}
      {activeNavId === 'realitems' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenRealItemForm}>
              Add Real Item
            </Button>
          </Box>
          {realItemsLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <RealItemList items={realItems} onEdit={handleEditRealItem} onDelete={handleDeleteRealItem} />
          )}
        </div>
      )}

      {/* Sets */}
      {activeNavId === 'sets' && (
        <div className={panelClass}>
          {setsLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <SetList characters={characters} />
          )}
        </div>
      )}

      {/* Dialogues */}
      {activeNavId === 'dialogues' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenFlowForm}>
              Add Flow
            </Button>
          </Box>
          {flowsLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <FlowList
              flows={flows}
              onOpenFlow={handleOpenFlowEditor}
              onEdit={handleEditFlow}
              onDelete={handleDeleteFlow}
              onBackups={handleBackups}
            />
          )}
          <FlowEditor
            open={flowEditorOpen}
            onClose={handleCloseFlowEditor}
            flow={currentFlow}
          />
                    <FlowBackupList
            open={backupDialogOpen}
            onClose={handleCloseBackupDialog}
            flowId={selectedFlowForBackups?._id}
            flowName={selectedFlowForBackups?.name}
            onRestore={() => {
              // Reload flows after restore
              fetchFlows();
            }}
          />
        </div>
      )}

      {/* Triggers */}
      {activeNavId === 'triggers' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenTriggerForm}>
              Add Trigger
            </Button>
          </Box>
          {triggersLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <TriggerList
              triggers={triggers}
              onEdit={handleEditTrigger}
              onDelete={handleDeleteTrigger}
            />
          )}
        </div>
      )}

      {/* Backgrounds / Locations (flow assets) */}
      {activeNavId === 'backgrounds' && (
        <div className={panelClass}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={bgAssetsTab}
              onChange={(_, v) => {
                if (v) {
                  setBgAssetsTab(v);
                  setBackgroundFormOpen(false);
                  setEditingBgAsset(null);
                }
              }}
            >
              <ToggleButton value="backgrounds">Backgrounds</ToggleButton>
              <ToggleButton value="locations">Locations</ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenBackgroundForm}>
              {bgAssetsTab === 'locations' ? 'Add location' : 'Add background'}
            </Button>
          </Box>
          {bgAssetsTab === 'locations' ? (
            locationsLoading ? (
              <Typography>Loading...</Typography>
            ) : (
              <BackgroundList
                items={locations}
                mode="location"
                onEdit={handleEditBgAsset}
                onDelete={handleDeleteLocation}
              />
            )
          ) : backgroundsLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <BackgroundList
              items={backgrounds}
              mode="background"
              onEdit={handleEditBgAsset}
              onDelete={handleDeleteBackground}
            />
          )}
        </div>
      )}

      {/* Scenes */}
      {activeNavId === 'scenes' && (
        <div className={panelClass}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenSceneForm}>
              Add Scene
            </Button>
          </Box>
          {scenesLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <SceneList
              scenes={scenes}
              onOpenScene={handleOpenSceneCanvas}
              onEditMeta={handleOpenSceneMetaForm}
              onDelete={handleDeleteScene}
            />
          )}
        </div>
      )}

      {/* Live2D animation controllers */}
      {activeNavId === 'animations' && (
        <div className={panelClass}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            Animations
          </Typography>
          <AnimControllersPanel onNotify={showSnackbar} />
        </div>
      )}

      {/* Currency Packs */}
      {activeNavId === 'currency' && (
        <div className={panelClass}>
          {currencyPacksLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <CurrencyPackList />
          )}
        </div>
      )}

      {/* Promo Codes */}
      {activeNavId === 'promocodes' && (
        <div className={panelClass}>
          {charactersLoading ? (
            <Typography>Loading characters…</Typography>
          ) : (
            <PromoCodesPanel characters={characters} />
          )}
        </div>
      )}

      {/* Game Config */}
      {activeNavId === 'gameconfig' && (
        <div className={panelClass}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
            Game Config
          </Typography>
          <GameConfig />
        </div>
      )}

      {/* Players */}
      {activeNavId === 'players' && (
        <div className={panelClass}>
          <PlayersPanel />
        </div>
      )}

      {/* AI Requests */}
      {activeNavId === 'ai' && (
        <div className={panelClass}>
          <GrokRequestForm characters={characters} kinks={kinks} items={items} />
        </div>
      )}

      {/* JOI */}
      {activeNavId === 'joi' && (
        <div className={panelClass}>
          <JoiPanel
            kinks={kinks}
            realItems={realItems}
            onNotify={(message, severity = 'info') => showSnackbar(message, severity)}
          />
        </div>
      )}

      {/* Character creation/editing form */}
      <CharacterForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        character={editingCharacter}
        isEdit={!!editingCharacter}
      />

      {/* Kink creation/editing form */}
      <KinkForm
        open={kinkFormOpen}
        onClose={handleCloseKinkForm}
        onSubmit={handleSubmitKinkForm}
        kink={editingKink}
        isEdit={!!editingKink}
      />

      {/* Real item creation/editing form */}
      <RealItemForm
        open={realItemFormOpen}
        onClose={() => { setRealItemFormOpen(false); setEditingRealItem(null); }}
        onSubmit={handleSubmitRealItem}
        item={editingRealItem}
        isEdit={!!editingRealItem}
      />

      {/* Flow creation/editing form */}
      <FlowForm
        open={flowFormOpen}
        onClose={handleCloseFlowForm}
        onSubmit={handleSubmitFlowForm}
        flow={editingFlow}
        isEdit={!!editingFlow}
      />

      {/* Trigger creation/editing form */}
      <TriggerForm
        open={triggerFormOpen}
        onClose={handleCloseTriggerForm}
        onSubmit={handleSubmitTriggerForm}
        trigger={editingTrigger}
        isEdit={!!editingTrigger}
      />

      <BackgroundForm
        open={backgroundFormOpen}
        onClose={handleCloseBackgroundForm}
        onSubmit={handleSubmitBackgroundForm}
        mode={bgAssetsTab === 'locations' ? 'location' : 'background'}
        record={editingBgAsset}
        isEdit={!!editingBgAsset}
      />

      <SceneForm
        open={sceneFormOpen}
        onClose={handleCloseSceneForm}
        onSubmit={handleCreateScene}
        characters={characters}
        flows={flows}
        submitting={sceneSubmitting}
      />

      <SceneMetaForm
        open={sceneMetaFormOpen}
        onClose={handleCloseSceneMetaForm}
        onSubmit={handleUpdateSceneMeta}
        scene={editingScene}
        characters={characters}
        flows={flows}
        submitting={sceneMetaSubmitting}
      />

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </div>
      </main>
    </div>
  );
};

export default AdminPage; 